import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormError } from '@typings/common';
import NewErrorSummary from '@components/NewErrorSummary';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
  isValidEmail,
} from '@utils/commons';
import { resendVerificationEmail } from '@api/auth';
import { ApiResponse } from '@typings/ApiResponse';
import getErrorMessage from '@utils/errorUtils';

const ResendActivitationEmail = () => {
  const [emailDetails, setEmailDetails] = useState({
    email: '',
    confirmEmail: '',
  });
  const [emailErrMessage, setEmailErrMessage] = useState<{
    email: string | undefined;
    confirmEmail: string | undefined;
  }>({
    email: '',
    confirmEmail: '',
  });
  const [errors, setErrors] = useState<FormError[]>([]);
  const [resendLoading, setResendLoading] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (value: string) => {
    if (!value) {
      return `Please enter your email address`;
    }

    if (!isValidEmail(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const checkMatchEmail = (newEmail: string, confirmEmail: string) => {
    if (newEmail.trim() !== confirmEmail.trim()) {
      return 'Email addresses must be the same';
    }
    return '';
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'email',
      validateEmail(emailDetails.email)
    );

    validationErrors = updateErrors(
      validationErrors,
      'confirmEmail',
      !emailDetails.confirmEmail ? 'Please re-enter your email address' : ''
    );

    if (!validateEmail(emailDetails.email) && emailDetails.confirmEmail) {
      validationErrors = updateErrors(
        validationErrors,
        'confirmEmail',
        checkMatchEmail(emailDetails.email, emailDetails.confirmEmail)
      );
    }
    setErrors(validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }
    try {
      setResendLoading(true);
      const res: ApiResponse<null> = await resendVerificationEmail(
        emailDetails.email
      );
      if (res?.status === 'success') {
        navigate(`/check-your-email/?email=${emailDetails.email}`);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = getErrorMessage(error);
      validationErrors = updateErrors(
        validationErrors,
        'resendActivationEmail',
        errorMessage
      );
      setErrors(validationErrors);
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    setEmailErrMessage({
      email: getErrorMessageFromErrors(errors, 'email'),
      confirmEmail: getErrorMessageFromErrors(errors, 'confirmEmail'),
    });
  }, [errors]);

  return (
    <div className="govuk-width-container mt-10 mb-10">
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-three-quarters">
          <h1 className="govuk-heading-xl">Resend your activation email</h1>
          <p className="govuk-body">
            Enter your email address below. If the email needs activating, we
            will send you the activation email. You need to click on the link in
            the email to verify your email address and activate your account.
          </p>
          <form>
            {errors.length > 0 && <NewErrorSummary errors={errors} />}

            <div
              className={`govuk-form-group ${emailErrMessage.email ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="email">
                Enter your email address
              </label>
              {emailErrMessage.email && (
                <span id="email-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {emailErrMessage.email}
                </span>
              )}
              <input
                className={`govuk-input govuk-input--width-20 ${emailErrMessage.email ? 'govuk-input--error' : ''}`}
                id="email"
                name="email"
                type="email"
                spellCheck="false"
                aria-describedby={
                  emailErrMessage.email ? 'new-email-error' : undefined
                }
                autoComplete="email"
                value={emailDetails.email}
                onChange={e =>
                  setEmailDetails(prev => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </div>
            <div
              className={`govuk-form-group ${emailErrMessage.confirmEmail ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="confirmEmail">
                Confirm your email address
              </label>
              {emailErrMessage.confirmEmail && (
                <span id="email-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {emailErrMessage.confirmEmail}
                </span>
              )}
              <input
                className={`govuk-input govuk-input--width-20 ${emailErrMessage.confirmEmail ? 'govuk-input--error' : ''}`}
                id="confirmEmail"
                name="confirmEmail"
                type="email"
                spellCheck="false"
                aria-describedby={
                  emailErrMessage.confirmEmail ? 'new-email-error' : undefined
                }
                autoComplete="email"
                value={emailDetails.confirmEmail}
                onChange={e =>
                  setEmailDetails(prev => ({
                    ...prev,
                    confirmEmail: e.target.value,
                  }))
                }
              />
            </div>
            <div className="govuk-button-group">
              <button
                type="submit"
                className="govuk-button"
                data-module="govuk-button"
                disabled={resendLoading}
                onClick={onSubmit}
              >
                Email me the link
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResendActivitationEmail;
